import { RegisterModel } from '@goparrot-dashboard-api/auth';
import { useEntityForm, ValidationStrategyEnum } from '@goparrot-dashboard/entity-form';
import { useI18n } from '@goparrot-dashboard/i18n';
import { Form } from '@goparrot-dashboard/shared-ui';
import * as React from 'react';

import { MarketButton } from '@market/react-bindings';
import { ENTER_KEY } from '../constants';
import { EmailFormSegment } from './segments/EmailFormSegment';
import { PasswordWithConfirmationFormSegment } from './segments/PasswordWithConfirmationFormSegment';

type RegisterFormProps = {
  onSubmit(payload: RegisterModel): void;
  isFormLoading?: boolean;
};
export const RegisterForm: React.FC<React.PropsWithChildren<RegisterFormProps>> = ({ onSubmit, isFormLoading = false }) => {
  const { getIntlString } = useI18n();
  const termsRef = React.useRef<HTMLParagraphElement>(null);

  const form = useEntityForm<RegisterModel>({
    model: RegisterModel,
    validationStrategy: ValidationStrategyEnum.onSave,
    initialState: {
      email: '',
      password: '',
      repeatPassword: '',
    },
  });

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    form.onSubmit(onSubmit);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key === ENTER_KEY) {
      if (termsRef.current?.contains(e.target as HTMLElement)) {
        return;
      }

      handleSubmit(e);
    }
  };

  const tosLink = (
    <a href="https://squareup.com/us/en/legal/general/franchise" target="_blank" rel="noreferrer">
      {getIntlString('cta.terms-of-service')}
    </a>
  );

  return (
    <Form onSubmit={handleSubmit} onKeyDown={handleKeyDown}>
      <EmailFormSegment<RegisterModel> form={form} />
      <PasswordWithConfirmationFormSegment<RegisterModel> form={form} />

      <p ref={termsRef} className={'tw-pt-6 tw-mb-0 tw-text-xs tw-text-gray-500'} role="note">
        {getIntlString('cta.terms-of-service-message', {
          values: {
            tosLink: tosLink,
          },
        })}
      </p>

      <MarketButton
        isLoading={isFormLoading || undefined}
        disabled={isFormLoading || (form.isDirtyForm && !form.isFormValid) || undefined}
        type="submit"
        className="tw-mt-5"
        rank="primary"
        data-testid="create-account-button"
        role="button"
      >
        {getIntlString('button.signUp')}
      </MarketButton>
    </Form>
  );
};
